/**
 * AnalysisChart Component
 * T119: Wrapper component for ApexCharts
 */

import { lazy, Suspense } from 'react';
import { cn } from '@/lib/utils';
import type { ChartConfig } from '@/types/api';

// Lazy load ApexCharts for better performance
const Chart = lazy(() => import('react-apexcharts'));

export interface AnalysisChartProps {
  /** Chart configuration */
  config: ChartConfig;
  /** Chart height in pixels */
  height?: number;
  /** Additional class names */
  className?: string;
}

/**
 * Chart loading fallback
 */
function ChartLoadingFallback({ height }: { height: number }) {
  return (
    <div
      className="flex items-center justify-center bg-[var(--glass-bg)] rounded-md animate-pulse"
      style={{ height }}
    >
      <span className="text-sm text-[var(--color-charcoal)]/50">
        Loading chart...
      </span>
    </div>
  );
}

/**
 * Splits a long label into multiple lines at word boundaries.
 * ApexCharts renders string[] categories as multi-line labels.
 */
function wrapLabel(label: string, maxCharsPerLine = 16): string | string[] {
  if (label.length <= maxCharsPerLine) return label;

  const words = label.split(/\s+/);
  const lines: string[] = [];
  let currentLine = '';

  for (const word of words) {
    if (currentLine && (currentLine.length + 1 + word.length) > maxCharsPerLine) {
      lines.push(currentLine);
      currentLine = word;
    } else {
      currentLine = currentLine ? `${currentLine} ${word}` : word;
    }
  }
  if (currentLine) lines.push(currentLine);

  return lines.length > 1 ? lines : label;
}

/**
 * Wrapper component for ApexCharts
 */
export function AnalysisChart({
  config,
  height = 350,
  className,
}: AnalysisChartProps) {
  const { type, series, options } = config;

  // For radar charts, wrap long xaxis labels into multi-line arrays
  const processedOptions = { ...options };
  if (type === 'radar' && processedOptions.xaxis?.categories) {
    processedOptions.xaxis = {
      ...processedOptions.xaxis,
      categories: processedOptions.xaxis.categories.map(
        (cat: string) => wrapLabel(cat)
      ) as unknown as string[],
    };
  }

  // Merge default options with provided options
  const chartOptions: ApexCharts.ApexOptions = {
    ...processedOptions,
    chart: {
      ...processedOptions?.chart,
      toolbar: {
        show: false,
        ...processedOptions?.chart?.toolbar,
      },
      background: 'transparent',
    },
    theme: {
      mode: 'light',
    },
    colors: processedOptions?.colors || ['var(--gradient-start)', 'var(--gradient-end)', '#6366f1', '#8b5cf6'],
  };

  return (
    <div className={cn('w-full', className)}>
      <Suspense fallback={<ChartLoadingFallback height={height} />}>
        <Chart
          type={type}
          series={series}
          options={chartOptions}
          height={height}
        />
      </Suspense>
    </div>
  );
}

export default AnalysisChart;
